import { useEffect, useState } from "react";
import {
  creditControlService,
  creditReason,
  type CreditStatus,
} from "@/services/creditControlService";
import { useIsOwner } from "@/hooks/useRole";

/**
 * Buyer credit position inside a sale modal, shown live as the buyer or amount
 * changes — the seller sees where the buyer stands *before* committing, which
 * is the only moment it can change the outcome.
 *
 * Shared by all four sale modals. A per-modal copy is how a money-path rule
 * ends up on three of four screens.
 *
 * Renders nothing when the farm hasn't enabled credit control, when there's no
 * buyer (walk-in), or when the buyer is within terms — a green "all fine" badge
 * on every sale would train people to ignore the row that matters.
 */
export default function BuyerCreditNotice({
  buyerId,
  amount,
  override,
  onOverrideChange,
}: {
  buyerId: number | null | undefined;
  amount: number;
  /** Owner's explicit approval to push a breaching sale through. */
  override: boolean;
  onOverrideChange: (v: boolean) => void;
}) {
  const isOwner = useIsOwner();
  const [status, setStatus] = useState<CreditStatus | null>(null);

  useEffect(() => {
    if (!buyerId) {
      setStatus(null);
      return;
    }
    let live = true;
    // Debounced: `amount` changes on every keystroke of the rate or quantity,
    // and each lookup is three queries. Stale responses are already discarded
    // by `live`, so this is about volume, not correctness.
    const t = setTimeout(() => {
      creditControlService
        .get(buyerId, amount)
        .then((s) => {
          if (live) setStatus(s);
        })
        // A failed lookup must not block the sale form. The server re-checks on
        // save, so the worst case is the seller finds out a moment later.
        .catch(() => {
          if (live) setStatus(null);
        });
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [buyerId, amount]);

  // Clear a stale override when the sale stops breaching — AND whenever the
  // buyer changes. Clearing only on "no longer breaching" left the tick in
  // place when switching between two blocked buyers, which is the case that
  // actually matters: an approval given for one buyer carrying to another.
  useEffect(() => {
    onOverrideChange(false);
    // Only on a buyer change; onOverrideChange is a stable setter in all callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId]);

  // Clear the tick whenever the checkbox that set it is no longer on screen —
  // which is `blocking`, not `ok`. A breaching buyer paid in full at the counter
  // is reported but not blocked, so the box disappears; on `ok` alone the
  // approval stayed in the payload with nothing visible to explain it.
  useEffect(() => {
    if ((!status || !status.blocking) && override) onOverrideChange(false);
  }, [status, override, onOverrideChange]);

  if (!status || status.mode === "off" || status.ok) return null;

  const reason = creditReason(status);

  if (!status.blocking) {
    // warn mode: say it plainly, change nothing.
    return (
      <div
        role="status"
        className="mt-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-400"
      >
        <span className="font-medium">This buyer is outside their credit terms.</span> {reason}
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400"
    >
      <span className="font-medium">This sale is blocked.</span> {reason}
      {isOwner ? (
        <label className="mt-2 flex items-start gap-2 font-normal">
          <input
            type="checkbox"
            id="credit-override"
            checked={override}
            onChange={(e) => onOverrideChange(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Approve this sale anyway. It will be recorded in the activity log against your name.
          </span>
        </label>
      ) : (
        <div className="mt-1">Ask the owner to approve it.</div>
      )}
    </div>
  );
}
